import { useMemo, useState } from 'react';
import {
  Avatar,
  Button,
  Card,
  Empty,
  Input,
  Popconfirm,
  Select,
  Space,
  Tag,
  Timeline,
  Typography,
  App,
} from 'antd';
import {
  CommentOutlined,
  DeleteOutlined,
  EditOutlined,
  PaperClipOutlined,
  SendOutlined,
  SyncOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { trpc } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { formatDateWithFallback } from '@/utils/dateFormat';
import { FileGallery } from '@/components/file/FileGallery';
import { ScheduleCommentSchema, type ScheduleComment } from '@shared/types/schedule-comment.types';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

type ScheduleStatus = 'scheduled' | 'in_progress' | 'pending_approval' | 'completed' | 'cancelled' | 'postponed';

interface ScheduleCommentListProps {
  scheduleId: string;
  editable?: boolean;
  /** 現在のスケジュールステータス */
  currentStatus?: ScheduleStatus;
  /** ステータス変更時のコールバック */
  onStatusChange?: () => void;
}

const STATUS_LABELS: Record<string, string> = {
  scheduled: '予定',
  in_progress: '実行中',
  pending_approval: '承認待ち',
  completed: '完了',
  cancelled: 'キャンセル',
  postponed: '延期',
};

const COMMENT_TYPE_COLORS: Record<string, string> = {
  manual: 'blue',
  status_change: 'green',
  assignment: 'purple',
  approval: 'orange',
};

const COMMENT_TYPE_LABELS: Record<string, string> = {
  manual: 'コメント',
  status_change: 'ステータス変更',
  assignment: '担当者変更',
  approval: '承認',
};

export function ScheduleCommentList({
  scheduleId,
  editable = true,
  currentStatus,
  onStatusChange,
}: ScheduleCommentListProps): JSX.Element {
  const { message } = App.useApp();
  const { user } = useAuthStore();

  const [newComment, setNewComment] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<ScheduleStatus | undefined>(undefined);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [expandedFileIds, setExpandedFileIds] = useState<Set<string>>(new Set());

  // コメント一覧を取得
  const { data: comments, isLoading, refetch } = trpc.scheduleComment.getComments.useQuery(
    { scheduleId },
    { enabled: Boolean(scheduleId) }
  );

  const normalizedComments = useMemo<ScheduleComment[]>(() => {
    const result = ScheduleCommentSchema.array().safeParse(comments ?? []);
    return result.success ? result.data : [];
  }, [comments]);

  // コメント作成
  const createCommentMutation = trpc.scheduleComment.createComment.useMutation({
    onSuccess: (data) => {
      const createdComment = ScheduleCommentSchema.safeParse(data);
      const isStatusChange = createdComment.success &&
        createdComment.data.commentType === 'status_change';
      if (isStatusChange) {
        message.success('ステータスを変更しました');
        onStatusChange?.();
      } else {
        message.success('コメントを投稿しました');
      }
      setNewComment('');
      setSelectedStatus(undefined);
      void refetch();
    },
    onError: (error) => {
      message.error(error.message || 'コメントの投稿に失敗しました');
    },
  });

  // コメント更新
  const updateCommentMutation = trpc.scheduleComment.updateComment.useMutation({
    onSuccess: () => {
      message.success('コメントを更新しました');
      setEditingCommentId(null);
      setEditingContent('');
      void refetch();
    },
    onError: (error) => {
      message.error(error.message || 'コメントの更新に失敗しました');
    },
  });

  // コメント削除
  const deleteCommentMutation = trpc.scheduleComment.deleteComment.useMutation({
    onSuccess: () => {
      message.success('コメントを削除しました');
      void refetch();
    },
    onError: (error) => {
      message.error(error.message || 'コメントの削除に失敗しました');
    },
  });

  const handleSubmit = () => {
    if (!newComment.trim()) return;
    createCommentMutation.mutate({
      scheduleId,
      content: newComment.trim(),
      newStatus: selectedStatus,
    });
  };

  const handleUpdate = (commentId: string) => {
    if (!editingContent.trim()) return;
    updateCommentMutation.mutate({
      commentId,
      content: editingContent.trim(),
    });
  };

  const handleDelete = (commentId: string) => {
    deleteCommentMutation.mutate({ commentId });
  };

  const startEditing = (commentId: string, content: string) => {
    setEditingCommentId(commentId);
    setEditingContent(content);
  };

  const cancelEditing = () => {
    setEditingCommentId(null);
    setEditingContent('');
  };

  const toggleFileExpand = (commentId: string) => {
    setExpandedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(commentId)) {
        next.delete(commentId);
      } else {
        next.add(commentId);
      }
      return next;
    });
  };

  const renderCommentContent = (comment: ScheduleComment) => {
    const isOwner = comment.userId === user?.id;
    const isManual = comment.commentType === 'manual';
    const isEditing = editingCommentId === comment.id;

    if (isEditing) {
      return (
        <Space direction="vertical" style={{ width: '100%' }}>
          <TextArea
            value={editingContent}
            onChange={(e) => setEditingContent(e.target.value)}
            autoSize={{ minRows: 2, maxRows: 6 }}
          />
          <Space>
            <Button
              type="primary"
              size="small"
              loading={updateCommentMutation.isPending}
              onClick={() => handleUpdate(comment.id)}
            >
              保存
            </Button>
            <Button size="small" onClick={cancelEditing}>
              キャンセル
            </Button>
          </Space>
        </Space>
      );
    }

    return (
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Space size={4} wrap>
          <Tag color={COMMENT_TYPE_COLORS[comment.commentType] ?? 'default'}>
            {COMMENT_TYPE_LABELS[comment.commentType] ?? comment.commentType}
          </Tag>
          {comment.statusFrom && comment.statusTo && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {STATUS_LABELS[comment.statusFrom] ?? comment.statusFrom}
              {' → '}
              {STATUS_LABELS[comment.statusTo] ?? comment.statusTo}
            </Text>
          )}
        </Space>
        <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
          {comment.content}
        </Paragraph>
        {/* アクションボタン行 */}
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<PaperClipOutlined />}
            onClick={() => toggleFileExpand(comment.id)}
          >
            {expandedFileIds.has(comment.id) ? '閉じる' : 'ファイル'}
          </Button>
          {/* 編集・削除ボタン（手動コメントかつ自分のコメントのみ） */}
          {editable && isOwner && isManual && (
            <>
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => startEditing(comment.id, comment.content)}
              >
                編集
              </Button>
              <Popconfirm
                title="コメントを削除しますか？"
                onConfirm={() => handleDelete(comment.id)}
                okText="削除"
                cancelText="キャンセル"
              >
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deleteCommentMutation.isPending}
                >
                  削除
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
        {/* ファイル添付エリア（展開時のみ表示） */}
        {expandedFileIds.has(comment.id) && (
          <div style={{ marginTop: 8 }}>
            <FileGallery
              entityType="schedule_comment"
              entityId={comment.id}
              editable={isOwner && isManual && editable}
              maxFiles={5}
              maxSize={5}
              accept="image/*"
            />
          </div>
        )}
      </Space>
    );
  };

  if (isLoading) {
    return (
      <Card loading size="small">
        <div style={{ height: 100 }} />
      </Card>
    );
  }

  return (
    <Card
      size="small"
      title={
        <Space>
          <CommentOutlined />
          <span>コメント・履歴</span>
          <Tag>{normalizedComments.length}</Tag>
        </Space>
      }
      extra={
        <Button
          type="text"
          icon={<SyncOutlined />}
          onClick={() => refetch()}
          loading={isLoading}
        >
          更新
        </Button>
      }
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {/* コメント入力フォーム */}
        {editable && (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <TextArea
              placeholder="コメントを入力..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 4 }}
              onPressEnter={(e) => {
                if (e.ctrlKey || e.metaKey) {
                  handleSubmit();
                }
              }}
            />
            <Space style={{ justifyContent: 'space-between', width: '100%', flexWrap: 'wrap' }}>
              <Space size={8}>
                {currentStatus && (
                  <Select
                    placeholder="ステータス変更"
                    value={selectedStatus}
                    onChange={setSelectedStatus}
                    allowClear
                    style={{ minWidth: 140 }}
                    options={Object.entries(STATUS_LABELS)
                      .filter(([key]) => key !== currentStatus)
                      .map(([value, label]) => ({ value, label }))}
                  />
                )}
                {selectedStatus && (
                  <Tag color="green">
                    {STATUS_LABELS[currentStatus ?? '']} → {STATUS_LABELS[selectedStatus]}
                  </Tag>
                )}
              </Space>
              <Space>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Ctrl+Enter で送信
                </Text>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  loading={createCommentMutation.isPending}
                  disabled={!newComment.trim()}
                  onClick={handleSubmit}
                >
                  {selectedStatus ? 'ステータス変更' : '送信'}
                </Button>
              </Space>
            </Space>
          </Space>
        )}

        {/* コメントリスト */}
        {normalizedComments.length > 0 ? (
          <Timeline
            items={normalizedComments.map((comment) => ({
              key: comment.id,
              color: COMMENT_TYPE_COLORS[comment.commentType] ?? 'gray',
              dot: comment.commentType === 'manual' ? (
                <Avatar
                  size="small"
                  src={comment.user?.avatarUrl}
                  icon={<UserOutlined />}
                />
              ) : undefined,
              children: (
                <div style={{ paddingBottom: 8 }}>
                  <Space size={8} style={{ marginBottom: 4 }}>
                    <Text strong>
                      {comment.user?.name ?? comment.user?.email ?? '不明なユーザー'}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {formatDateWithFallback(comment.createdAt, { showTime: true })}
                    </Text>
                  </Space>
                  {renderCommentContent(comment)}
                </div>
              ),
            }))}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="コメントはありません"
          />
        )}
      </Space>
    </Card>
  );
}
